import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useToast } from 'vue-toastification'
import api from '@/services/api'
import { useAuthStore } from './auth'

export const useOrdersStore = defineStore('orders', () => {
    // State con dati iniziali mock
    const orders = ref([
        {
            id: 1234,
            customer_name: 'Mario Rossi',
            customer_email: 'mario.rossi@email.com',
            total_amount: 45.50,
            status: 'pending',
            payment_status: 'pending',
            delivery_method: 'Ritiro in negozio',
            delivery_address: '',
            notes: 'Torta per compleanno',
            created_at: new Date().toISOString(),
            date_needed: new Date().toISOString()
        },
        {
            id: 1235,
            customer_name: 'Laura Bianchi',
            customer_email: 'laura.bianchi@email.com',
            total_amount: 128.00,
            status: 'processing',
            payment_status: 'paid',
            delivery_method: 'Consegna a domicilio',
            delivery_address: 'Via Roma 123, Milano',
            notes: 'Consegna entro le 15:00',
            created_at: new Date(Date.now() - 3600000).toISOString(),
            date_needed: new Date().toISOString()
        },
        {
            id: 1236,
            customer_name: 'Giuseppe Verdi',
            customer_email: 'giuseppe.verdi@email.com',
            total_amount: 67.00,
            status: 'ready',
            payment_status: 'paid',
            delivery_method: 'Ritiro in negozio',
            delivery_address: '',
            notes: '',
            created_at: new Date(Date.now() - 7200000).toISOString(),
            date_needed: new Date().toISOString()
        },
        {
            id: 1237,
            customer_name: 'Anna Neri',
            customer_email: 'anna.neri@email.com',
            total_amount: 89.90,
            status: 'completed',
            payment_status: 'paid',
            delivery_method: 'Ritiro in negozio',
            delivery_address: '',
            notes: 'Cliente molto soddisfatto',
            created_at: new Date(Date.now() - 86400000).toISOString(),
            date_needed: new Date(Date.now() - 86400000).toISOString()
        }
    ])
    const currentOrder = ref(null)
    const todayOrders = ref([])
    const isLoading = ref(false)
    const filters = ref({
        status: null,
        dateFrom: null,
        dateTo: null,
        storeId: null,
        clientId: null,
        search: '',
        sortBy: 'date_needed',
        sortOrder: 'asc'
    })
    const pagination = ref({
        page: 1,
        perPage: 20,
        total: 4,
        totalPages: 1
    })

    // Stati ordine con colori
    const orderStatuses = {
        new: { label: 'Nuovo', color: 'order-new', icon: 'plus-circle' },
        confirmed: { label: 'Confermato', color: 'order-confirmed', icon: 'check-circle' },
        working: { label: 'In Lavorazione', color: 'order-working', icon: 'clock' },
        ready: { label: 'Pronto', color: 'order-ready', icon: 'gift' },
        delivered: { label: 'Consegnato', color: 'order-delivered', icon: 'truck' },
        cancelled: { label: 'Annullato', color: 'order-cancelled', icon: 'x-circle' }
    }

    const toast = useToast()
    const authStore = useAuthStore()

    // Getters
    const filteredOrders = computed(() => {
        let result = [...orders.value]

        // Filtra per status
        if (filters.value.status) {
            result = result.filter(o => o.status === filters.value.status)
        }

        // Filtra per date
        if (filters.value.dateFrom) {
            result = result.filter(o => new Date(o.date_needed) >= new Date(filters.value.dateFrom))
        }
        if (filters.value.dateTo) {
            result = result.filter(o => new Date(o.date_needed) <= new Date(filters.value.dateTo))
        }

        // Filtra per store
        if (filters.value.storeId) {
            result = result.filter(o => o.store_id === filters.value.storeId)
        }

        // Filtra per cliente
        if (filters.value.clientId) {
            result = result.filter(o => o.client_id === filters.value.clientId)
        }

        // Filtra per ricerca
        if (filters.value.search) {
            const searchLower = filters.value.search.toLowerCase()
            result = result.filter(o =>
                o.client?.name?.toLowerCase().includes(searchLower) ||
                o.client?.phone?.includes(searchLower) ||
                o.notes?.toLowerCase().includes(searchLower) ||
                o.id?.toString().includes(searchLower)
            )
        }

        // Ordina
        result.sort((a, b) => {
            let comparison = 0
            switch (filters.value.sortBy) {
                case 'date_needed':
                    comparison = new Date(a.date_needed) - new Date(b.date_needed)
                    break
                case 'created_at':
                    comparison = new Date(a.created_at) - new Date(b.created_at)
                    break
                case 'total':
                    comparison = a.total - b.total
                    break
                case 'status':
                    comparison = a.status.localeCompare(b.status)
                    break
                default:
                    comparison = 0
            }
            return filters.value.sortOrder === 'asc' ? comparison : -comparison
        })

        return result
    })

    const ordersByStatus = computed(() => {
        const grouped = {}
        Object.keys(orderStatuses).forEach(status => {
            grouped[status] = orders.value.filter(o => o.status === status)
        })
        return grouped
    })

    const pendingOrders = computed(() => {
        return orders.value.filter(o =>
            ['new', 'confirmed', 'working'].includes(o.status)
        )
    })

    const ordersForToday = computed(() => {
        const today = new Date().toISOString().split('T')[0]
        return orders.value.filter(o =>
            o.date_needed.split('T')[0] === today
        )
    })

    const ordersForTomorrow = computed(() => {
        const tomorrow = new Date()
        tomorrow.setDate(tomorrow.getDate() + 1)
        const tomorrowStr = tomorrow.toISOString().split('T')[0]
        return orders.value.filter(o =>
            o.date_needed.split('T')[0] === tomorrowStr
        )
    })

    const orderStats = computed(() => {
        const stats = {
            total: orders.value.length,
            pending: pendingOrders.value.length,
            today: ordersForToday.value.length,
            tomorrow: ordersForTomorrow.value.length,
            revenue: orders.value.reduce((sum, o) => sum + (o.total || 0), 0)
        }
        return stats
    })

    // Actions
    const fetchOrders = async (params = {}) => {
        isLoading.value = true
        try {
            const response = await api.get('/orders', {
                params: {
                    page: pagination.value.page,
                    per_page: pagination.value.perPage,
                    ...filters.value,
                    ...params
                }
            })

            // Response format: { success: true, orders: [...], total: N, page: 1, per_page: 20 }
            orders.value = response.data.orders || []
            pagination.value = {
                page: response.data.page || 1,
                perPage: response.data.per_page || 20,
                total: response.data.total || 0,
                totalPages: Math.ceil((response.data.total || 0) / (response.data.per_page || 20))
            }

            return true
        } catch (error) {
            console.error('Fetch orders error:', error)

            // Fallback mock data quando API non funziona
            if (orders.value.length === 0) {
                orders.value = [
                    {
                        id: 1234,
                        customer_name: 'Mario Rossi',
                        customer_email: 'mario.rossi@email.com',
                        total_amount: 45.50,
                        status: 'pending',
                        payment_status: 'pending',
                        delivery_method: 'Ritiro in negozio',
                        delivery_address: '',
                        notes: 'Torta per compleanno',
                        created_at: new Date().toISOString(),
                        date_needed: new Date().toISOString()
                    },
                    {
                        id: 1235,
                        customer_name: 'Laura Bianchi',
                        customer_email: 'laura.bianchi@email.com',
                        total_amount: 128.00,
                        status: 'processing',
                        payment_status: 'paid',
                        delivery_method: 'Consegna a domicilio',
                        delivery_address: 'Via Roma 123, Milano',
                        notes: 'Consegna entro le 15:00',
                        created_at: new Date(Date.now() - 3600000).toISOString(),
                        date_needed: new Date().toISOString()
                    },
                    {
                        id: 1236,
                        customer_name: 'Giuseppe Verdi',
                        customer_email: 'giuseppe.verdi@email.com',
                        total_amount: 67.00,
                        status: 'ready',
                        payment_status: 'paid',
                        delivery_method: 'Ritiro in negozio',
                        delivery_address: '',
                        notes: '',
                        created_at: new Date(Date.now() - 7200000).toISOString(),
                        date_needed: new Date().toISOString()
                    },
                    {
                        id: 1237,
                        customer_name: 'Anna Neri',
                        customer_email: 'anna.neri@email.com',
                        total_amount: 89.90,
                        status: 'completed',
                        payment_status: 'paid',
                        delivery_method: 'Ritiro in negozio',
                        delivery_address: '',
                        notes: 'Cliente molto soddisfatto',
                        created_at: new Date(Date.now() - 86400000).toISOString(),
                        date_needed: new Date(Date.now() - 86400000).toISOString()
                    }
                ]

                pagination.value = {
                    page: 1,
                    perPage: 20,
                    total: orders.value.length,
                    totalPages: 1
                }

                console.log('Using mock orders data due to API failure')
            }

            return false
        } finally {
            isLoading.value = false
        }
    }

    const fetchOrder = async (id) => {
        isLoading.value = true
        try {
            const response = await api.get(`/orders/${id}`)
            currentOrder.value = response.data
            return response.data
        } catch (error) {
            console.error('Fetch order error:', error)
            toast.error('Errore nel caricamento dell\'ordine')
            return null
        } finally {
            isLoading.value = false
        }
    }

    const fetchOrderItems = async (orderId) => {
        try {
            const response = await api.get(`/orders/${orderId}/items`)
            return response.data
        } catch (error) {
            console.error('Fetch order items error:', error)
            // Mock fallback data
            return [
                {
                    id: 1,
                    product_id: 1,
                    product_name: 'Torta al Cioccolato',
                    quantity: 1,
                    price: 25.00
                },
                {
                    id: 2,
                    product_id: 2,
                    product_name: 'Cannoli Siciliani',
                    quantity: 6,
                    price: 3.50
                }
            ]
        }
    }

    const totalOrders = computed(() => pagination.value.total || orders.value.length)

    const fetchTodayOrders = async () => {
        isLoading.value = true
        try {
            const today = new Date().toISOString().split('T')[0]
            const response = await api.get('/orders/today', {
                params: {
                    date: today,
                    store_id: authStore.storeId
                }
            })
            todayOrders.value = response.data
            return true
        } catch (error) {
            console.error('Fetch today orders error:', error)
            toast.error('Errore nel caricamento degli ordini di oggi')
            return false
        } finally {
            isLoading.value = false
        }
    }

    const createOrder = async (orderData) => {
        isLoading.value = true
        try {
            const response = await api.post('/orders', orderData)
            orders.value.unshift(response.data)
            toast.success('Ordine creato con successo')
            return response.data
        } catch (error) {
            console.error('Create order error:', error)
            toast.error(error.response?.data?.message || 'Errore nella creazione dell\'ordine')
            return null
        } finally {
            isLoading.value = false
        }
    }

    const updateOrder = async (id, orderData) => {
        isLoading.value = true
        try {
            const response = await api.put(`/orders/${id}`, orderData)
            const index = orders.value.findIndex(o => o.id === id)
            if (index !== -1) {
                orders.value[index] = response.data
            }
            if (currentOrder.value?.id === id) {
                currentOrder.value = response.data
            }
            toast.success('Ordine aggiornato con successo')
            return response.data
        } catch (error) {
            console.error('Update order error:', error)
            toast.error(error.response?.data?.message || 'Errore nell\'aggiornamento dell\'ordine')
            return null
        } finally {
            isLoading.value = false
        }
    }

    const updateOrderStatus = async (id, status) => {
        isLoading.value = true
        try {
            const response = await api.put(`/orders/${id}/status`, { status })
            const index = orders.value.findIndex(o => o.id === id)
            if (index !== -1) {
                orders.value[index] = response.data
            }
            if (currentOrder.value?.id === id) {
                currentOrder.value = response.data
            }

            const statusInfo = orderStatuses[status]
            toast.success(`Ordine ${statusInfo.label.toLowerCase()}`)

            // Emetti evento per aggiornamento real-time
            window.dispatchEvent(new CustomEvent('order-status-changed', {
                detail: { orderId: id, status, order: response.data }
            }))

            return response.data
        } catch (error) {
            console.error('Update order status error:', error)
            toast.error(error.response?.data?.message || 'Errore nell\'aggiornamento dello stato')
            return null
        } finally {
            isLoading.value = false
        }
    }

    const deleteOrder = async (id) => {
        isLoading.value = true
        try {
            await api.delete(`/orders/${id}`)
            orders.value = orders.value.filter(o => o.id !== id)
            toast.success('Ordine eliminato con successo')
            return true
        } catch (error) {
            console.error('Delete order error:', error)
            toast.error(error.response?.data?.message || 'Errore nell\'eliminazione dell\'ordine')
            return false
        } finally {
            isLoading.value = false
        }
    }

    const duplicateOrder = async (id) => {
        isLoading.value = true
        try {
            const response = await api.post(`/orders/${id}/duplicate`)
            orders.value.unshift(response.data)
            toast.success('Ordine duplicato con successo')
            return response.data
        } catch (error) {
            console.error('Duplicate order error:', error)
            toast.error(error.response?.data?.message || 'Errore nella duplicazione dell\'ordine')
            return null
        } finally {
            isLoading.value = false
        }
    }

    const printOrder = async (id) => {
        try {
            const response = await api.get(`/orders/${id}/print`, {
                responseType: 'blob'
            })

            // Crea un URL per il blob e apri in una nuova finestra
            const blob = new Blob([response.data], { type: 'application/pdf' })
            const url = window.URL.createObjectURL(blob)
            window.open(url, '_blank')

            toast.success('Documento di stampa generato')
            return true
        } catch (error) {
            console.error('Print order error:', error)
            toast.error('Errore nella generazione del documento')
            return false
        }
    }

    const generateDailyReport = async (date) => {
        isLoading.value = true
        try {
            const response = await api.get('/orders/daily-report', {
                params: {
                    date,
                    store_id: authStore.storeId
                },
                responseType: 'blob'
            })

            // Download del report
            const blob = new Blob([response.data], { type: 'application/pdf' })
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `report-${date}.pdf`
            link.click()

            toast.success('Report giornaliero generato')
            return true
        } catch (error) {
            console.error('Generate daily report error:', error)
            toast.error('Errore nella generazione del report')
            return false
        } finally {
            isLoading.value = false
        }
    }

    const setFilters = (newFilters) => {
        filters.value = { ...filters.value, ...newFilters }
        pagination.value.page = 1
    }

    const resetFilters = () => {
        filters.value = {
            status: null,
            dateFrom: null,
            dateTo: null,
            storeId: null,
            clientId: null,
            search: '',
            sortBy: 'date_needed',
            sortOrder: 'asc'
        }
        pagination.value.page = 1
    }

    const changePage = (page) => {
        pagination.value.page = page
        fetchOrders()
    }

    // Handlers per aggiornamenti real-time
    const handleOrderUpdate = (orderData) => {
        const index = orders.value.findIndex(o => o.id === orderData.id)
        if (index !== -1) {
            orders.value[index] = orderData
        } else {
            orders.value.unshift(orderData)
        }

        if (currentOrder.value?.id === orderData.id) {
            currentOrder.value = orderData
        }
    }

    return {
        // State
        orders,
        currentOrder,
        todayOrders,
        isLoading,
        filters,
        pagination,
        orderStatuses,

        // Getters
        filteredOrders,
        ordersByStatus,
        pendingOrders,
        ordersForToday,
        ordersForTomorrow,
        orderStats,

        // Actions
        fetchOrders,
        fetchOrder,
        fetchTodayOrders,
        createOrder,
        updateOrder,
        updateOrderStatus,
        deleteOrder,
        duplicateOrder,
        printOrder,
        generateDailyReport,
        setFilters,
        resetFilters,
        changePage,
        handleOrderUpdate
    }
}, {
    persist: {
        paths: ['filters']
    }
})
